import { Injectable } from '@angular/core';
import { AbstractControlOptions, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TipoFirmante } from '../../envios/models/tipo-firmante';
import { ContenidoDocVisible } from '../models/contenido-doc-visible';
import { ContenidoDocVisibleGuardar } from '../models/contenido-doc-visible-guardar';
import { CasillaDibujo, ConfiguracionFirma, FirmaDibujo, FirmanteNif, PosicionFirmaConFirmante, PosicionFormulario, PosicionFormularioConFirmante, TipoFirma } from '../models/firmante-nif';
import { TipoDocumento, TipoDocumentoClass } from '../models/tipo-documento';
import { cifValidators,  formDocValidators, posicionCifValidators,  validacionCopiaBasica,  validacionGenerico, validacionNombre, validacionNombreArchivoFirmado, validacionSplit } from './customValidatorsDocs';
import { buildFormularioDocumentoVacio } from './formulario-documento-factory';
import { DatosDocumentosService } from './datos-documentos.service';
import { DatosConfiguracionDocumentosService } from './datos-configuracion-documentos.service';
import { normalizarDocumentoEntradaApi } from './documento-entrada-api-normalizer';
import { environment } from 'src/environments/environment';
import { TiposConfiguracion } from 'src/environments/Configuration/TiposConfiguracion';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MedidaPdf, RectPixel } from './formulario-documento-coordenadas';
import { CampoControlLocation, CasillaControlLocation, FirmaControlLocation, isCampoRowColocada, isCasillaRowColocada, isFirmaRowColocada, isPalabraRowColocada, locateCampoControl, locateCasillaControl, locateFirmaControl, locatePalabraControl, normalizedCoordsFromPixelRect, OverlayDocumentoTipo, PalabraControlLocation, projectCamposFromForm, projectCasillasFromForm, projectFirmasFromForm, projectPalabrasFromForm, UpdateCampoPositionPatch, UpdateCasillaPositionPatch, UpdatePalabraPositionPatch } from './formulario-documento-overlays';
import { TiposContenido } from '../models/tipos-contenido';
import { ActualizarFormularioGrupoBorradorPatch, buildFormularioGrupoBorradorGroup, FormularioGrupoBorrador, readFormularioGrupoBorrador } from './formulario-grupo-borrador';

@Injectable({
  providedIn: 'root'
})
export class FormularioDocumentoService {  /** Medida del canvas PDF en el paso upload; usada por conversiones centralizadas (fase 2+). */
  medidaPdfViewport: MedidaPdf | null = null;
  public formularioDocumento: FormGroup = new FormGroup({});
  documentos: TipoDocumento[] = [];
  oldDoc: TipoDocumento;
  idTipoDocOrigen: number;
  totalPages: number;
  objetoDocumentoEnviar: TipoDocumento = {} as TipoDocumento;
  /** Metadatos de formularios en panel paso 10; no implica filas en `firmantesNif[].formularios`. */
  formulariosBorradorFormArray: FormArray = new FormArray([]);
  cancelarCopiaDoc: boolean = false;
  indexPaso: number = 0;
  objetoPredicate:{firmantes: FormArray, index: number, getIndexFirmaPanel: (codigo: string) => number};
  private indexFirmaPanel = new Map<string, number>();
  private firmaPanelChanged$ = new Subject<void>();
  documentosAll: TipoDocumento[] = [];
  documentosCargados$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  documentoAEditar: TipoDocumento = new TipoDocumentoClass();
  private pendienteCapturarBaseline = false;

  constructor(public fb: FormBuilder,
              public datosDocumentosService: DatosDocumentosService,
              private _snackBar: MatSnackBar,
              private datosConfiguracionDocumentosService: DatosConfiguracionDocumentosService) {
                this.montarFormularioDocVacio();
               }

  // ---------------------------------------------------------------------------
  // Inicialización del formulario documento
  // ---------------------------------------------------------------------------

  montarFormularioDocVacio(){
    this.formularioDocumento = buildFormularioDocumentoVacio(this.fb);
  }

  inicializaFormularioDoc(id: number){
    if(id==0){
      this.formulariosBorradorFormArray = new FormArray([]);
      this.formularioDocumento = this.fb.group({
        idTipoDocumento: [null],
        nombre: ['', [validacionNombre(this.documentosAll, id), Validators.pattern(/^[^']*$/), Validators.maxLength(250)]],
        urlDocTemp: [''],
        idPdf:[''],
        idTipoDocOrigen:[null],
        copyConfiguracion: [false],
        generico:[, validacionGenerico(() => this.indexPaso)],
        incluirContenido:[false],
        tiposContenido: this.fb.array([]),
        texto:[''],
        textoFormulario:['', Validators.maxLength(200)],
        textoAExcluir:[''],
        textoAExcluirFormulario:['', Validators.maxLength(200)],
        contieneCif:[false],
        posicionCif:[, posicionCifValidators(()=>this.contieneCif)],
        cifOrden:[, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifOrden')],
        cifPage:[, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifPage')],
        cifFormulario:[, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifFormulario')],
        cif:[''],
        cifResueltoPage:[null],
        cifResueltoOrden:[null],
        firmantesNif: this.fb.array([]),
        paginas:[this.totalPages],
        esCopiaBasica:[false, validacionCopiaBasica(()=>this.formularioDocumento)],
        split:['', [validacionSplit(this.totalPages), Validators.maxLength(50)]],
        formatoNombreArchivoAuditTrail:['{NOMBRE_EMPLEADO}_{APELLIDO1_EMPLEADO}_{APELLIDO2_EMPLEADO}_{NOMBRE_DOCUMENTO}_AuditTrail.pdf', validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'audit')],
        formatoNombreArchivoFirmado:['{NOMBRE_EMPLEADO}_{APELLIDO1_EMPLEADO}_{APELLIDO2_EMPLEADO}_{NOMBRE_DOCUMENTO}.pdf', validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'doc')],
        esRLT:[false]
      })
    }else{
      this.oldDoc = this.documentosAll.find(x => x.idTipoDocumento == id);
      this.formularioDocumento = this.fb.group({
        idTipoDocumento: [this.oldDoc.idTipoDocumento],
        nombre: [this.oldDoc.nombre, [validacionNombre(this.documentosAll, id), Validators.pattern(/^[^']*$/), Validators.maxLength(250)]],
        // urlDocTemp: [this.oldDoc.urlDocTemp],
        idPdf:[this.oldDoc.idPdf],
        idTipoDocOrigen: [this.oldDoc.idTipoDocOrigen],
        copyConfiguracion: [],
        generico:[this.oldDoc.generico, validacionGenerico(() => this.indexPaso)],
        incluirContenido:[false],
        tiposContenido: this.fb.array([]),
        texto:[this.oldDoc.texto],
        textoFormulario:[this.oldDoc.textoFormulario, Validators.maxLength(200)],
        textoAExcluir:[this.oldDoc.textoAExcluir],
        textoAExcluirFormulario:[this.oldDoc.textoAExcluirFormulario, Validators.maxLength(200)],
        contieneCif:[this.contieneDatos(this.oldDoc.cifOrden, this.oldDoc.cifPage,this.oldDoc.cifFormulario)],
        posicionCif:[this.buscarPosicionCif(this.oldDoc.cifOrden, this.oldDoc.cifPage,this.oldDoc.cifFormulario), posicionCifValidators(()=>this.contieneCif)],
        cifOrden:[this.oldDoc.cifOrden, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifOrden')],
        cifPage:[this.oldDoc.cifPage, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifPage')],
        cifFormulario:[this.oldDoc.cifFormulario, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifFormulario')],
        cif:[''],
        cifResueltoPage:[null],
        cifResueltoOrden:[null],
        firmantesNif: this.fb.array([]),
        paginas:[this.oldDoc.paginas],
        esCopiaBasica:[this.oldDoc.esCopiaBasica, validacionCopiaBasica(()=>this.formularioDocumento)],
        split:[this.oldDoc.split, [validacionSplit(this.totalPages), Validators.maxLength(50)]],
        formatoNombreArchivoAuditTrail:[this.oldDoc.formatoNombreArchivoAuditTrail, validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'audit')],
        formatoNombreArchivoFirmado:[this.oldDoc.formatoNombreArchivoFirmado, validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'doc')],
        esRLT:[this.oldDoc.esRLT]
      })
      this.hydrateOverlaysFromDocumento(this.oldDoc);

    }
    this.marcarPendienteCapturarBaseline();
  }

  inicializaFormularioFirma(firmanteNif: FirmanteNif){
    let nuevoFormularioFirma = this.fb.group({
        tipoFirma: [firmanteNif.firmas.tipoFirma],
        nombreCertificado: [firmanteNif.firmas.nombreCertificado],
        tieneTag: [firmanteNif.firmas.tieneTag],
        nombreTag: [firmanteNif.firmas.nombreTag],
        posicionFirmas:
          Array.isArray(firmanteNif.firmas.posicionFirmas) &&
          firmanteNif.firmas.posicionFirmas.length > 0
            ? this.fb.array(
                firmanteNif.firmas.posicionFirmas.map((pf) =>
                  this.newPosFirmas({ ...pf, firmante: firmanteNif.tipoFirmante })
                )
              )
            : this.fb.array([])
      },{ validator: [formDocValidators([() => this.objetoPredicate,() => this.formularioDocumento])]} as AbstractControlOptions);
      return nuevoFormularioFirma
  }

  getDocumentosDataAll(){
    this.documentosCargados$.next(false);
    if(environment.Configuracion== TiposConfiguracion.V2){
      this.documentosCargados$.next(true);
      return;
    }
    this.datosDocumentosService.getTipoDocumentos([],0).subscribe(
      next => {
        this.documentosAll = next;
        this.aplicarParchePruebaRltEnDocumentosAll();
        this.documentosCargados$.next(true);
      },
      error => {
        this.documentosCargados$.next(false);
        if(error.status!==401) this._snackBar.open(error.message, 'Cerrar',{duration: 4000});
      }
    )
  }

  copiaTipoDocumento(doc: TipoDocumento){
    this.cancelarCopiaTipoDocumento() //cancelamos copia primero para borrar si hubiera datos en el formulario
    this.cancelarCopiaDoc = false;
    this.formularioDocumento = this.fb.group({
      idTipoDocumento: [this.idTipoDocumento.value],
      nombre: [this.nombre.value, [validacionNombre(this.documentosAll, this.idTipoDocumento.value), Validators.pattern(/^[^']*$/), Validators.maxLength(250)]],
      idPdf:[this.idPdf.value],
      idTipoDocOrigen: [doc.idTipoDocumento],
      copyConfiguracion: [true],
      generico:[doc.generico, validacionGenerico(() => this.indexPaso)],
      incluirContenido:[this.contienePalabrasClave(doc.tiposContenido)],
      tiposContenido: this.buildPalabrasFormArray(doc.tiposContenido ?? []),
      texto:[doc.texto],
      textoFormulario:[doc.textoFormulario, Validators.maxLength(200)],
      textoAExcluir:[doc.textoAExcluir],
      textoAExcluirFormulario:[doc.textoFormulario, Validators.maxLength(200)],
      contieneCif:[this.contieneDatos(doc.cifOrden, doc.cifPage,doc.cifFormulario)],
      posicionCif:[this.buscarPosicionCif(doc.cifOrden, doc.cifPage,doc.cifFormulario), posicionCifValidators(()=>this.contieneCif)],
      cifOrden:[doc.cifOrden, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifOrden')],
      cifPage:[doc.cifPage, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifPage')],
      cifFormulario:[doc.cifFormulario, cifValidators([() => this.contieneCif, () => this.posicionCif], this.totalPages, 'cifFormulario')],
      cif:[''],
      cifResueltoPage:[null],
      cifResueltoOrden:[null],
      firmantesNif: this.setExistingFirmantesNif(doc.firmantesNif),
      paginas:[this.paginas.value],
      esCopiaBasica:[doc.esCopiaBasica, validacionCopiaBasica(()=>this.formularioDocumento)],
      split:[doc.split, [validacionSplit(this.totalPages), Validators.maxLength(50)]],
      formatoNombreArchivoAuditTrail:[doc.formatoNombreArchivoAuditTrail, validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'audit')],
      formatoNombreArchivoFirmado:[doc.formatoNombreArchivoFirmado, validacionNombreArchivoFirmado(()=> this.formularioDocumento, 'doc')],
      esRLT: [doc.esRLT]
    })
    this.setExisitingFirmasArray(doc.firmantesNif);
    this.hydrateBorradoresFromFormularios();
    this.datosConfiguracionDocumentosService.solicitarRefreshOverlaysDocumento();
    this.marcarPendienteCapturarBaseline();
  }

  cancelarCopiaTipoDocumento(){
    this.cancelarCopiaDoc = true;
    this.formularioDocumento = this.fb.group({
      idTipoDocumento: [this.idTipoDocumento.value],
      nombre: [this.nombre.value, [validacionNombre(this.documentosAll, this.idTipoDocumento.value), Validators.pattern(/^[^']*$/), Validators.maxLength(250)]],
      idPdf:[this.idPdf.value],
      idTipoDocOrigen:[null],
      copyConfiguracion: [false],
      generico:[],
      incluirContenido:[false],
      tiposContenido: this.fb.array([]),
      texto:[''],
      textoFormulario:[''],
      textoAExcluirFormulario:[''],
      textoAExcluir:[''],
      contieneCif:[false],
      posicionCif:[],
      cifOrden:[],
      cifPage:[],
      cifFormulario:[],
      cif:[''],
      cifResueltoPage:[null],
      cifResueltoOrden:[null],
      firmantesNif: this.fb.array([]),
      paginas:[this.paginas.value],
      esCopiaBasica:[],
      split:[''],
      formatoNombreArchivoAuditTrail:[''],
      formatoNombreArchivoFirmado:[''],
      esRLT:[false]
    })
    this.datosConfiguracionDocumentosService.transferirDatosPalabra([])
    this.datosConfiguracionDocumentosService.transferirArrayFirmasVacio([])
    this.datosConfiguracionDocumentosService.transferirDatosCasilla([])
    this.datosConfiguracionDocumentosService.transferirDatosCampo([])
    this.datosConfiguracionDocumentosService.transferirDocId({})
    this.setExisitingFormulariosArray(null)
    this.formulariosBorradorFormArray = new FormArray([]);
  }

  resetFormularioDocumento(){
    this.cancelarCopiaDoc = true;
    this.formularioDocumento = this.fb.group({
      idTipoDocumento: [null],
      nombre: [''],
      urlDocTemp: [''],
      idPdf:[''],
      idTipoDocOrigen:[null],
      copyConfiguracion: [false],
      generico:[],
      incluirContenido:[false],
      tiposContenido: this.fb.array([]),
      texto:[''],
      textoFormulario:[''],
      textoAExcluir:[''],
      textoAExcluirFormulario:[''],
      contieneCif:[false],
      posicionCif:[],
      cifOrden:[],
      cifPage:[],
      cifFormulario:[],
      cif:[''],
      cifResueltoPage:[null],
      cifResueltoOrden:[null],
      firmantesNif: this.fb.array([]),
      paginas:[this.paginas.value],
      esCopiaBasica:[],
      split:[''],
      formatoNombreArchivoAuditTrail:[''],
      formatoNombreArchivoFirmado:[''],
      esRLT:[false]
    })
    this.datosConfiguracionDocumentosService.transferirDatosPalabra([])
    this.datosConfiguracionDocumentosService.transferirArrayFirmasVacio([])
    this.datosConfiguracionDocumentosService.transferirDatosCasilla([])
    this.datosConfiguracionDocumentosService.transferirDatosCampo([])
    this.datosConfiguracionDocumentosService.transferirDocId({})
    this.setExisitingFormulariosArray(null)
    this.formulariosBorradorFormArray = new FormArray([]);
    this.documentoAEditar = new TipoDocumentoClass();
    this.pendienteCapturarBaseline = false;
    this.datosConfiguracionDocumentosService.clearTiposFirmanteCatalogo();
  }

  marcarPendienteCapturarBaseline(): void {
    this.pendienteCapturarBaseline = true;
  }

  capturarDocumentoAEditarSiPendiente(): void {
    if (!this.pendienteCapturarBaseline) {
      return;
    }
    const tienePdf = !!this.formularioDocumento?.get('idPdf')?.value;
    if (tienePdf && !this.medidaPdfViewport) {
      return;
    }
    this.capturarDocumentoAEditar();
    this.pendienteCapturarBaseline = false;
  }

  capturarDocumentoAEditar(): void {
    const snapshot = this.montarObjetoDocumentoGuardar();
    this.documentoAEditar = JSON.parse(JSON.stringify(snapshot));
    delete this.documentoAEditar.paginas;
  }

  /**
   * Hidrata overlays (palabras, firmas, casillas, campos) desde payload API en formularioDocumento.
   * Las coordenadas se conservan en formato normalizado (sin conversion pixel en esta fase).
   */
  hydrateOverlaysFromDocumento(doc: TipoDocumento): void {
    if (!this.formularioDocumento) {
      return;
    }
    normalizarDocumentoEntradaApi(doc);
    const palabras = doc.tiposContenido ?? [];
    const firmantes = doc.firmantesNif ?? [];

    this.formularioDocumento.setControl('tiposContenido', this.buildPalabrasFormArray(palabras));
    this.formularioDocumento.setControl('firmantesNif', this.setExistingFirmantesNif(firmantes));
    this.setExisitingFirmasArray(firmantes);

    const incluirContenido = this.formularioDocumento.get('incluirContenido');
    if (incluirContenido) {
      incluirContenido.setValue(this.contienePalabrasClave(palabras));
    }
    this.hydrateBorradoresFromFormularios();
  }

  // ---------------------------------------------------------------------------
  // Getters y accesores del formulario
  // ---------------------------------------------------------------------------

  get esRLT(){ return this.formularioDocumento?.get('esRLT')}

  get documentosCargados(): boolean { return this.documentosCargados$.value; }

  get nombre(){ return this.formularioDocumento.controls['nombre']}

  get contieneCif(){ return this.formularioDocumento.get('contieneCif').value }

  get posicionCif(){ return this.formularioDocumento.get('posicionCif').value}

  get firmantesNif(){ return this.formularioDocumento.get('firmantesNif') as FormArray}

  get tiposContenido(): FormArray { return this.formularioDocumento.get('tiposContenido') as FormArray}

  get idTipoDocumento(){ return this.formularioDocumento.controls['idTipoDocumento']}

  get idPdf(){ return this.formularioDocumento.controls['idPdf']}

  get paginas(){ return this.formularioDocumento.controls['paginas']}

  // Firmas (posicionFirmas en firmantesNif[].firmas — SSOT para overlays)

  /** FormGroup `firmas` del firmante en firmantesNif (SSOT paso 9). */
  getFirmasGroup(firmanteCodigo: string): FormGroup | null {
    const index = this.firmantesNif.value.findIndex(
      (x: FirmanteNif) => x.tipoFirmante.codigo === firmanteCodigo
    );
    if (index === -1) {
      return null;
    }
    const firmas = (this.firmantesNif.at(index) as FormGroup).get('firmas');
    return firmas instanceof FormGroup ? firmas : null;
  }

  getIndexFirmaPanel(firmanteCodigo: string): number {
    return this.indexFirmaPanel.get(firmanteCodigo) ?? 1;
  }

  setIndexFirmaPanel(firmanteCodigo: string, indexFirma: number): void {
    this.indexFirmaPanel.set(firmanteCodigo, indexFirma);
    this.notifyFirmasStateChanged();
  }

  resetIndexFirmaPanel(): void {
    this.indexFirmaPanel.clear();
    this.notifyFirmasStateChanged();
  }

  firmaPanelChanged(): Observable<void> {
    return this.firmaPanelChanged$.asObservable();
  }

  /** Notifica botonera/panel cuando cambian firmas o se reemplaza firmantesNif. */
  notifyFirmasStateChanged(): void {
    this.firmaPanelChanged$.next();
  }

  firmaCompletaParaFirmante(firmanteCodigo: string): boolean {
    const firmas = this.getFirmasGroup(firmanteCodigo);
    if (!firmas) {
      return false;
    }
    const tipo: TipoFirma = firmas.get('tipoFirma')?.value;
    if (!tipo) {
      return false;
    }

    let completa: boolean;
    if (tipo === TipoFirma.SinFirma || tipo === TipoFirma.Validador) {
      completa = true;
    } else if (tipo !== TipoFirma.Dibujada && tipo !== TipoFirma.Certificado) {
      return false;
    } else {
      const tieneTag = firmas.get('tieneTag')?.value;
      if (tieneTag === null || tieneTag === undefined) {
        return false;
      }
      if (tieneTag === true) {
        const nombreTag = firmas.get('nombreTag')?.value as string;
        if (!nombreTag) {
          return false;
        }
        completa = /^\S*$/.test(nombreTag) && nombreTag.length <= 200;
      } else {
        const posicionFirmas = firmas.get('posicionFirmas') as FormArray;
        completa = posicionFirmas != null && posicionFirmas.length > 0;
      }
    }

    return completa && firmas.valid;
  }

  todosFirmantesConFirmaCompleta(): boolean {
    if (!this.firmantesNif?.length) {
      return false;
    }
    return this.firmantesNif.controls.every((ctrl) => {
      const codigo = (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo as string;
      return !!codigo && this.firmaCompletaParaFirmante(codigo);
    });
  }

  contieneDatos(orden: number, page: number,cifFormulario:string): boolean{
    if(orden || page||cifFormulario) return true
    return false
  }

  buscarPosicionCif(orden: number, page: number,cifFormulario:string):string{
    if(orden && page) return 'pagina'
    if(cifFormulario) return 'formulario'
    return null
  }

  contienePalabrasClave(tiposContenido: ContenidoDocVisibleGuardar[]): boolean{
    if(tiposContenido.length > 0) return true
    return false
  }

  // ---------------------------------------------------------------------------
  // Validación, refresh y predicate
  // ---------------------------------------------------------------------------

  setPdfViewport(medida: MedidaPdf): void {
    this.medidaPdfViewport = medida;
  }

  //para hacer la validación del formulario firma y obtener el índice del momento
  updateObjetoPredicate(){
    this.objetoPredicate = {
      firmantes: this.firmantesNif,
      index: this.indexPaso,
      getIndexFirmaPanel: (codigo: string) => this.getIndexFirmaPanel(codigo),
    };
  }

  refrescaDocumento(){
    try{
      if(this.formularioDocumento.controls){
        this.formularioDocumento.controls['cifFormulario'].updateValueAndValidity();
        this.formularioDocumento.controls['cifOrden'].updateValueAndValidity();
        this.formularioDocumento.controls['cifPage'].updateValueAndValidity();
        this.formularioDocumento.controls['contieneCif'].updateValueAndValidity();
        this.formularioDocumento.controls['posicionCif'].updateValueAndValidity();
        this.formularioDocumento.controls['copyConfiguracion'].updateValueAndValidity();
        this.formularioDocumento.controls['esCopiaBasica'].updateValueAndValidity();
        this.refreshFirmantesNif();
        this.formularioDocumento.controls['formatoNombreArchivoAuditTrail'].updateValueAndValidity();
        this.formularioDocumento.controls['formatoNombreArchivoFirmado'].updateValueAndValidity();
        this.formularioDocumento.controls['generico'].updateValueAndValidity();
        this.formularioDocumento.controls['idPdf'].updateValueAndValidity();
        this.formularioDocumento.controls['idTipoDocOrigen'].updateValueAndValidity();
        this.formularioDocumento.controls['idTipoDocumento'].updateValueAndValidity();
        this.formularioDocumento.controls['incluirContenido'].updateValueAndValidity();
        this.formularioDocumento.controls['nombre'].updateValueAndValidity();
        this.formularioDocumento.controls['paginas'].updateValueAndValidity();
        this.formularioDocumento.controls['split'].updateValueAndValidity();
        this.formularioDocumento.controls['texto'].updateValueAndValidity();
        this.formularioDocumento.controls['textoAExcluir'].updateValueAndValidity();
        this.formularioDocumento.controls['textoAExcluirFormulario'].updateValueAndValidity();
        this.formularioDocumento.controls['textoFormulario'].updateValueAndValidity();
        this.refreshTipocontenido();
        this.formularioDocumento.updateValueAndValidity();
      }
    }catch(ex){
      console.error(ex);
    }
  }

  refreshTipocontenido(){
    let tiposContenido = this.formularioDocumento.controls['tiposContenido'] as FormArray;
    //Recorremos el from group
    if(tiposContenido!=null && tiposContenido.length>0){
      for(let i=0;i<tiposContenido.length;i++){
        let element = tiposContenido.at(i) as FormGroup;
        element.controls['id'].updateValueAndValidity();
        element.controls['pagina'].updateValueAndValidity();
        element.controls['posicionHeight'].updateValueAndValidity();
        element.controls['posicionRight'].updateValueAndValidity();
        element.controls['posicionTop'].updateValueAndValidity();
        element.controls['posicionWidth'].updateValueAndValidity();
        element.controls['tipo'].updateValueAndValidity();
        element.controls['identificador'].updateValueAndValidity();
        element.controls['nombre'].updateValueAndValidity();
        element.updateValueAndValidity();
      }
    }
    this.formularioDocumento.controls['tiposContenido'].updateValueAndValidity();
  }

  refreshFirmantesNif(){
    let firmantesNif = this.formularioDocumento.controls['firmantesNif'] as FormArray;
      //Recorremos el from group
      if(firmantesNif!=null && firmantesNif.length>0){
        for(let i=0;i<firmantesNif.length;i++){
          let element = firmantesNif.at(i) as FormGroup;
          this.refreshCamposEditables(element);
          this.refresFirmas((element.controls['firmas'] as FormGroup));
          this.refreshFormulario((element.controls['formularios'] as FormArray));
          element.controls['nifFormulario'].updateValueAndValidity();
          element.controls['nifOrden'].updateValueAndValidity();
          element.controls['nifPage'].updateValueAndValidity();
          element.controls['tipoFirmante'].updateValueAndValidity();
          // element.controls['posicionNif'].updateValueAndValidity();
          element.updateValueAndValidity();
        }
      }
      this.formularioDocumento.controls['firmantesNif'].updateValueAndValidity();
  }

  refreshCamposEditables(element:FormGroup){
    let camposEditables =  element.controls['camposEditables'] as FormArray;
    if(camposEditables!=null && camposEditables.length>0){
      for(let i=0;i<camposEditables.length;i++){
        let element = camposEditables.at(i) as FormGroup;

          element.controls['id'].updateValueAndValidity();
          element.controls['pagina'].updateValueAndValidity();
          element.controls['posicionHeight'].updateValueAndValidity();
          element.controls['posicionLeft'].updateValueAndValidity();
          element.controls['posicionRight'].updateValueAndValidity();
          element.controls['posicionTop'].updateValueAndValidity();
          element.controls['posicionWidth'].updateValueAndValidity();
          element.controls['identificador'].updateValueAndValidity();
          element.controls['nombreCampo'].updateValueAndValidity();
          element.controls['valorPorDefecto'].updateValueAndValidity();
          element.controls['obligatorio'].updateValueAndValidity();
          element.updateValueAndValidity();
      }
      camposEditables.updateValueAndValidity();
    }
  }

  refresFirmas(element:FormGroup){
      element.controls['nombreCertificado'].updateValueAndValidity();
      element.controls['nombreTag'].updateValueAndValidity();
      this.refreshPosicionFirmas(element);
      element.controls['tieneTag'].updateValueAndValidity();
      element.controls['tipoFirma'].updateValueAndValidity();
  }

  refreshPosicionFirmas(element:FormGroup){
    let posiconFirmas =  element.controls['posicionFirmas'] as FormArray;
    if(posiconFirmas!=null && posiconFirmas.length>0){
      for(let i=0;i<posiconFirmas.length;i++){
        let element = posiconFirmas.at(i) as FormGroup;
          element.controls['firmante'].updateValueAndValidity();
          element.controls['id'].updateValueAndValidity();
          element.controls['pagina'].updateValueAndValidity();
          element.controls['posicionHeight'].updateValueAndValidity();
          element.controls['posicionLeft'].updateValueAndValidity();
          element.controls['posicionRight'].updateValueAndValidity();
          element.controls['posicionTop'].updateValueAndValidity();
          element.controls['posicionWidth'].updateValueAndValidity();
          element.updateValueAndValidity();
      }
      posiconFirmas.updateValueAndValidity();
    }
  }

  refreshFormulario(formularios:FormArray){
    if(formularios!=null && formularios.length>0){
      for(let i=0;i<formularios.length;i++){
        let element = formularios.at(i) as FormGroup;
          element.controls['firmante']?.updateValueAndValidity();
          element.controls['id']?.updateValueAndValidity();
          element.controls['pagina']?.updateValueAndValidity();
          element.controls['posicionHeight']?.updateValueAndValidity();
          element.controls['posicionLeft']?.updateValueAndValidity();
          element.controls['posicionRight']?.updateValueAndValidity();
          element.controls['posicionTop']?.updateValueAndValidity();
          element.controls['posicionWidth']?.updateValueAndValidity();
          element.controls['nombre']?.updateValueAndValidity();
          element.controls['orden']?.updateValueAndValidity();
          element.controls['valorOpcionSeleccionada']?.updateValueAndValidity();
          element.controls['obligatorio']?.updateValueAndValidity();
          element.controls['idNombreFormulario'].updateValueAndValidity();
          element.updateValueAndValidity();
      }
      formularios.updateValueAndValidity();
    }
  }

  //refrescamos los valores de todos los controles para que salga el resultado también en value y no solo dentro de los controles
  refreshFormValues(values:any){
    for (const field in values.controls) { // 'field' is a string
      const control = values.get(field); // 'control' is a FormControl
      control.updateValueAndValidity();
    }
  }

  // ---------------------------------------------------------------------------
  // Proyección overlays PDF (todos los tipos)
  // ---------------------------------------------------------------------------

  /**
   * Proyecta overlays del formulario a coordenadas de pantalla (casillas / campos / firmas / palabras).
   * Requiere `setPdfViewport` con la medida del visor PDF activo.
   */
getOverlaysForPage(tipo: 'casilla', page?: number, firmante?: TipoFirmante | null): CasillaDibujo[];

getOverlaysForPage(tipo: 'campo', page?: number, firmante?: TipoFirmante | null): CasillaDibujo[];

getOverlaysForPage(tipo: 'firma', page?: number, firmante?: TipoFirmante | null): FirmaDibujo[];

getOverlaysForPage(tipo: 'palabra', page?: number, firmante?: TipoFirmante | null): ContenidoDocVisible[];

getOverlaysForPage(tipo: OverlayDocumentoTipo, page?: number, firmante?: TipoFirmante | null): CasillaDibujo[] | FirmaDibujo[] | ContenidoDocVisible[] {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return [];
    }
    if (tipo === 'casilla') {
      return projectCasillasFromForm(this.firmantesNif, this.medidaPdfViewport, page, firmante);
    }
    if (tipo === 'campo') {
      return projectCamposFromForm(this.firmantesNif, this.medidaPdfViewport, page, firmante);
    }
    if (tipo === 'firma') {
      return projectFirmasFromForm(this.firmantesNif, this.medidaPdfViewport, page, firmante);
    }
    if (tipo === 'palabra') {
      return projectPalabrasFromForm(this.tiposContenido, this.medidaPdfViewport, page);
    }
    return [];
  }

  // ---------------------------------------------------------------------------
  // Palabras clave (tiposContenido)
  // ---------------------------------------------------------------------------

  buildPalabrasFormArray(palabrasClave: ContenidoDocVisibleGuardar[]): FormArray {
    const formArrayPalabras = new FormArray([]);
    palabrasClave.forEach(x => formArrayPalabras.push(this.fb.group({
        id: [x.id],
        pagina: [x.pagina],
        tipo: [x.tipo],
        posicionTop: [x.posicionTop],
        posicionLeft: [x.posicionLeft],
        posicionRight: [x.posicionRight],
        posicionHeight: [x.posicionHeight],
        posicionWidth: [x.posicionWidth],
        identificador: [(!x.identificador || x.identificador === '') ? `Contenido_${x.id}` : x.identificador],
        nombre: [x.nombre ?? '']
    })));
    return formArrayPalabras;
  }

  // Palabras (tiposContenido — SSOT para overlays)

  updatePalabraPosition(
    id: number,
    rectPixel: RectPixel,
    patch?: UpdatePalabraPositionPatch
  ): boolean {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return false;
    }

    const located = locatePalabraControl(this.tiposContenido, id);
    if (!located) {
      return false;
    }

    const norm = normalizedCoordsFromPixelRect(rectPixel, this.medidaPdfViewport);
    located.group.patchValue({
      posicionTop: norm.posicionTop,
      posicionLeft: norm.posicionLeft,
      posicionRight: norm.posicionRight,
      posicionHeight: norm.posicionHeight,
      posicionWidth: norm.posicionWidth,
      ...(patch?.page !== undefined ? { pagina: patch.page } : {}),
      ...(patch?.tipo !== undefined ? { tipo: patch.tipo } : {}),
      ...(patch?.identificador !== undefined ? { identificador: patch.identificador } : {}),
      ...(patch?.nombre !== undefined ? { nombre: patch.nombre } : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.tiposContenido);
    this.refreshFormValues(this.formularioDocumento);
    return true;
  }

  addPalabraFromPixel(
    rect: RectPixel,
    meta: {
      page: number;
      tipo: TiposContenido;
      identificador?: string;
      nombre?: string;
    }
  ): number {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return -1;
    }

    const norm = normalizedCoordsFromPixelRect(rect, this.medidaPdfViewport);
    const id = this.nextPalabraId();
    const identificador = (meta.identificador ?? '').trim() || `Contenido_${id}`;

    this.tiposContenido.push(
      this.fb.group({
        id: [id],
        pagina: [meta.page],
        tipo: [meta.tipo],
        posicionTop: [norm.posicionTop],
        posicionLeft: [norm.posicionLeft],
        posicionRight: [norm.posicionRight],
        posicionHeight: [norm.posicionHeight],
        posicionWidth: [norm.posicionWidth],
        identificador: [identificador],
        nombre: [meta.nombre ?? ''],
      })
    );

    this.refreshFormValues(this.tiposContenido);
    this.refreshFormValues(this.formularioDocumento);
    return id;
  }

  removePalabraById(id: number): void {
    const located = this.locatePalabraById(id);
    if (!located) {
      return;
    }

    this.tiposContenido.removeAt(located.palabraIndex);
    this.refreshFormValues(this.tiposContenido);
    this.refreshFormValues(this.formularioDocumento);
  }

  updatePalabraExtraInfo(
    id: number,
    patch: { identificador?: string; nombre?: string }
  ): void {
    const located = this.locatePalabraById(id);
    if (!located) {
      return;
    }

    located.group.patchValue({
      ...(patch.identificador !== undefined ? { identificador: patch.identificador } : {}),
      ...(patch.nombre !== undefined ? { nombre: patch.nombre } : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.tiposContenido);
    this.refreshFormValues(this.formularioDocumento);
  }

  clearAllPalabras(): void {
    while (this.tiposContenido.length > 0) {
      this.tiposContenido.removeAt(0);
    }
    this.refreshFormValues(this.tiposContenido);
    this.refreshFormValues(this.formularioDocumento);
  }

  private locatePalabraById(id: number): PalabraControlLocation | null {
    return locatePalabraControl(this.tiposContenido, id);
  }

  private nextPalabraId(): number {
    const ids: number[] = [];
    this.tiposContenido.value?.forEach((row: { id?: number }) => {
      if (row?.id != null) {
        ids.push(row.id);
      }
    });
    return ids.length === 0 ? 0 : Math.max(...ids) + 1;
  }

  // ---------------------------------------------------------------------------
  // Firmantes NIF (paso 8)
  // ---------------------------------------------------------------------------

  //creamos newArray con los valores existentes del formArray, le añadimos el nuevo firmante o actualizamos y se lo pasamos a setExisitngFirmantesNif para que convierta el array en formArray y lo asigne al atributo "firmantesNif" del formulario. Si ya exite el firmante solo actualizamos los valores que han podido cambiar. Dejamos Firmas, Formularios y CamposEditables como están (antes se estaban borrando todos y está mal).
  addFirmanteNifInFormArray(firmanteNif: FirmanteNif){
    let newArray = this.firmantesNif.value
    let index = newArray.findIndex((x:FirmanteNif) => x.tipoFirmante.codigo == firmanteNif.tipoFirmante.codigo)

    if(index > -1){
      // newArray[index] = firmanteNif
      newArray[index].tipoFirmante.desc = firmanteNif.tipoFirmante.desc
      newArray[index].tipoFirmante.codigo = firmanteNif.tipoFirmante.codigo
      newArray[index].nifPage = firmanteNif.nifPage
      newArray[index].nifOrden = firmanteNif.nifOrden
      newArray[index].nifFormulario = firmanteNif.nifFormulario
      newArray[index].posicionNif = firmanteNif.posicionNif
      if (firmanteNif.nif !== undefined) {
        newArray[index].nif = firmanteNif.nif
      }
    }else newArray.push(firmanteNif)

    this.formularioDocumento.controls['firmantesNif'] = this.setExistingFirmantesNif(newArray)

    this.setExisitingFirmasArray(newArray);
    this.notifyFirmasStateChanged();
  }

  deleteFirmanteNifInFormArray(firmanteNif: FirmanteNif){
    let newArray = this.firmantesNif.value
    let index = newArray.findIndex((x:FirmanteNif) => x.tipoFirmante.codigo == firmanteNif.tipoFirmante.codigo)
    if(index > -1){
      newArray.splice(index,1)
      this.formularioDocumento.controls['firmantesNif'] = this.setExistingFirmantesNif(newArray)
    }
    this.formularioDocumento.controls['firmantesNif'] = this.renombrarFirmantesAdicionales();
    this.notifyFirmasStateChanged();
  }

  /**
   * Actualiza `firmantesNif` en un FormGroup arbitrario (p. ej. store Envio simple)
   * reutilizando `setExistingFirmantesNif` / `setExisitingFirmasArray`.
   */
  actualizarFirmantesNifEnFormulario(formulario: FormGroup, firmantes: FirmanteNif[]): void {
    const anterior = this.formularioDocumento;
    this.formularioDocumento = formulario;
    try {
      formulario.setControl('firmantesNif', this.setExistingFirmantesNif(firmantes));
      this.setExisitingFirmasArray(firmantes);
    } finally {
      this.formularioDocumento = anterior;
    }
  }

  /**
   * Anade un firmante vacio al final del FormArray sin reconstruir los existentes
   * (evita perder overlays al propagar roster en Envio simple).
   */
  appendFirmanteVacioEnFormulario(formulario: FormGroup, codigo: string, desc: string): void {
    const anterior = this.formularioDocumento;
    this.formularioDocumento = formulario;
    try {
      const yaExiste = this.firmantesNif.controls.some(
        ctrl => (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo === codigo
      );
      if (yaExiste) {
        return;
      }
      const vacio: FirmanteNif = {
        tipoFirmante: { codigo, desc },
        nifPage: 0,
        nifOrden: 0,
        nifFormulario: '',
        posicionNif: '',
        firmas: {
          tipoFirma: null as unknown as TipoFirma,
          nombreCertificado: '',
          tieneTag: false,
          nombreTag: '',
          posicionFirmas: []
        },
        formularios: [],
        camposEditables: []
      };
      const grupo = this.setExistingFirmantesNif([vacio]).at(0);
      this.firmantesNif.push(grupo);
    } finally {
      this.formularioDocumento = anterior;
    }
  }

  /** Elimina firmantes y overlays asociados al cambiar genérico ↔ no genérico. */
  limpiarDatosPorCambioGenerico(resetearCif: boolean): void {
    this.formularioDocumento.setControl('firmantesNif', this.fb.array([]));
    this.formulariosBorradorFormArray = new FormArray([]);
    this.datosConfiguracionDocumentosService.noExistenNifs = [];
    this.datosConfiguracionDocumentosService.transferirArrayFirmasVacio([]);
    this.datosConfiguracionDocumentosService.transferirDatosCasilla([]);
    this.datosConfiguracionDocumentosService.transferirDatosCampo([]);
    this.datosConfiguracionDocumentosService.resetAbrirFirmantePanel();
    this.datosConfiguracionDocumentosService.solicitarRefreshOverlaysDocumento();
    this.notifyFirmasStateChanged();

    if (resetearCif) {
      this.formularioDocumento.patchValue({
        contieneCif: false,
        posicionCif: null,
        cifOrden: null,
        cifPage: null,
        cifFormulario: null,
        cif: '',
        cifResueltoPage: null,
        cifResueltoOrden: null,
      });
      this.datosConfiguracionDocumentosService.nohayCif = false;
    }
  }

  renombrarFirmantesAdicionales(){
    let minCodigo = 103;
    let adicionales = this.firmantesNif.value.filter((x:FirmanteNif) => x.tipoFirmante.desc.includes('Firmante adicional'));

    // ordenamos según código, el '+' los convierte en números en vez de string
    adicionales.sort((a, b) => +a.tipoFirmante.codigo - +b.tipoFirmante.codigo);

    // asignamos los nuevos códigos por orden
    for (let firmante of adicionales) {
      firmante.tipoFirmante.codigo = minCodigo.toString();
      firmante.tipoFirmante.desc = `Firmante adicional ${minCodigo - 102}`;
      minCodigo++;
    }
    //actualizamos el array de firmantesNif con firmas, formularios y campos
    this.formularioDocumento.controls['firmantesNif'] = this.setExistingFirmantesNif(this.firmantesNif.value)
    this.setExisitingFirmasArray(this.firmantesNif.value);

    return this.firmantesNif;
  }

  RequiereNif():boolean{
    if (this.formularioDocumento.controls['generico'].value || this.formularioDocumento.controls['esCopiaBasica'].value  ){
      return false;
    }else{
      return true;
    }
  }

  setExistingFirmantesNif(firmantesNif: FirmanteNif[]): FormArray{
    const formArray = new FormArray([]);
    let objetoPredicateNifPage:{arrayFirmantes: FirmanteNif[], nifInfo: number, index: number}
    let objetoPredicateNifOrden:{arrayFirmantes: FirmanteNif[], nifInfo: number, index: number}
    let objetoPredicateNifFormulario:{arrayFirmantes: FirmanteNif[], nifInfo: null, index: number}
    let permiteSinNif = (this.formularioDocumento.controls['generico'].value || this.formularioDocumento.controls['esCopiaBasica'].value  )
    firmantesNif.forEach(x => {
      let index = firmantesNif.findIndex(y=> y.tipoFirmante.desc == x.tipoFirmante.desc )
      objetoPredicateNifPage={
        arrayFirmantes: this.firmantesNif.value,
        nifInfo: x.nifPage,
        index: index
      }
      objetoPredicateNifOrden={
        arrayFirmantes: this.firmantesNif.value,
        nifInfo: x.nifOrden,
        index: index
      }
      objetoPredicateNifFormulario={
        arrayFirmantes: this.firmantesNif.value,
        nifInfo: null,
        index: index
      }
      formArray.push(this.fb.group({
        tipoFirmante: this.fb.group({
          codigo:[x.tipoFirmante.codigo],
          desc:[x.tipoFirmante.desc]
        }),
        nifPage: [x.nifPage],
        nifOrden: [x.nifOrden],
        nifFormulario:[x.nifFormulario],
        nif: [x.nif ?? ''],
        posicionNif:[x.posicionNif],
        firmas: this.inicializaFormularioFirma(x),
        formularios: this.setExisitingFormulariosArray(x),
        camposEditables: this.setExisitingCamposArray(x)
      }))
    })
    return formArray
  }

  // ---------------------------------------------------------------------------
  // Firmas (paso 9, firmantesNif[].firmas)
  // ---------------------------------------------------------------------------

  newPosFirmas(x:PosicionFirmaConFirmante):FormGroup{
    return this.fb.group({
      id: [x.id],
      pagina: [x.pagina],
      firmante: [x.firmante],
      posicionTop: [x.posicionTop],
      posicionLeft: [x.posicionLeft],
      posicionRight: [x.posicionRight],
      posicionHeight: [x.posicionHeight],
      posicionWidth: [x.posicionWidth]
    })
  }

  //cargamos cada firmanteNif que viene de la api y añadimos el array de posicionFirmas al correspondiente firmanteNif del form.
  setExisitingFirmasArray(firmantesNifArray: FirmanteNif[]){
    firmantesNifArray.forEach(x=>{
      const isEmpty = Object.keys(x.firmas).length;
      if(x && isEmpty !== 0 && x.firmas.posicionFirmas?.length > 0){
        const index = this.firmantesNif.value.findIndex((y:FirmanteNif) => y.tipoFirmante.desc == x.tipoFirmante.desc)
        let firmasArray = x.firmas.posicionFirmas;
        let formArrayFirmasFirmante = new FormArray([]);
        firmasArray.forEach(z=>{
          formArrayFirmasFirmante.push(this.fb.group({
            id: [z.id],
            pagina: [z.pagina],
            firmante:[x.tipoFirmante],
            posicionTop: [z.posicionTop],
            posicionLeft: [z.posicionLeft],
            posicionRight: [z.posicionRight],
            posicionHeight: [z.posicionHeight],
            posicionWidth: [z.posicionWidth]
          }))
        })

        let posicionFirmantesForm = this.firmantesNif.at(index).get('firmas') as FormGroup;
        posicionFirmantesForm.controls['posicionFirmas'] = formArrayFirmasFirmante;
        this.refreshFormValues(posicionFirmantesForm)
        this.refreshFormValues(posicionFirmantesForm.controls['posicionFirmas'])
      }
    })
  }

  /** Vacía posicionFirmas y notifica upload al cambiar tipoFirma (cualquier transición). */
  clearFirmasPdfOnTipoChange(firmanteCodigo: string, tipoAnterior: TipoFirma, tipoNuevo: TipoFirma, notifyUpload = true): void {
    if (tipoAnterior === tipoNuevo) {
      return;
    }
    const firmas = this.getFirmasGroup(firmanteCodigo);
    if (!firmas) {
      return;
    }
    const posicionFirmas = firmas.get('posicionFirmas') as FormArray;
    while (posicionFirmas?.length > 0) {
      posicionFirmas.removeAt(0, { emitEvent: false });
    }
    if (notifyUpload) {
      const firmante = (this.firmantesNif.value as FirmanteNif[]).find(
        (f) => f.tipoFirmante.codigo === firmanteCodigo
      )?.tipoFirmante;
      if (firmante) {
        this.datosConfiguracionDocumentosService.transferirArrayFirmasVacio(firmante);
      }
    }
    posicionFirmas.updateValueAndValidity({ emitEvent: false });
    firmas.updateValueAndValidity({ emitEvent: false });
    this.notifyFirmasStateChanged();
  }

  /** Elimina formularios, borradores y campos editables del firmante al pasar a Validador; notifica PDF/panel. */
  clearFormulariosYCamposOnValidador(firmanteCodigo: string, notifyUpload = true): void {
    const firmanteNif = this.firmantesNif.controls.find(
      (ctrl) => (ctrl as FormGroup).get('tipoFirmante')?.value?.codigo === firmanteCodigo
    ) as FormGroup | undefined;
    if (!firmanteNif) {
      return;
    }

    const formularios = firmanteNif.get('formularios') as FormArray;
    const camposEditables = firmanteNif.get('camposEditables') as FormArray;
    while (formularios?.length > 0) {
      formularios.removeAt(0, { emitEvent: false });
    }
    while (camposEditables?.length > 0) {
      camposEditables.removeAt(0, { emitEvent: false });
    }
    for (let i = this.formulariosBorradorFormArray.length - 1; i >= 0; i--) {
      const row = this.formulariosBorradorFormArray.at(i) as FormGroup;
      if (row.get('firmante')?.value?.codigo === firmanteCodigo) {
        this.formulariosBorradorFormArray.removeAt(i);
      }
    }

    formularios?.updateValueAndValidity({ emitEvent: false });
    camposEditables?.updateValueAndValidity({ emitEvent: false });
    this.formulariosBorradorFormArray.updateValueAndValidity({ emitEvent: false });
    this.refreshFormValues(firmanteNif);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);

    if (!notifyUpload) {
      return;
    }
    const firmante = (this.firmantesNif.value as FirmanteNif[]).find(
      (f) => f.tipoFirmante.codigo === firmanteCodigo
    )?.tipoFirmante;
    if (firmante) {
      this.datosConfiguracionDocumentosService.transferirArrayFormulariosVacio(firmante);
      this.datosConfiguracionDocumentosService.transferirArrayCamposEditablesVacio(firmante);
    }
  }

  updateFirmaPosition(id: number, firmanteCodigo: string, rectPixel: RectPixel, patch?: { page?: number }): boolean {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return false;
    }

    const located = locateFirmaControl(this.firmantesNif, id, firmanteCodigo);
    if (!located) {
      return false;
    }

    const norm = normalizedCoordsFromPixelRect(rectPixel, this.medidaPdfViewport);
    located.group.patchValue({
      posicionTop: norm.posicionTop,
      posicionLeft: norm.posicionLeft,
      posicionRight: norm.posicionRight,
      posicionHeight: norm.posicionHeight,
      posicionWidth: norm.posicionWidth,
      ...(patch?.page !== undefined ? { pagina: patch.page } : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    return true;
  }

  addFirmaFromPixel(rect: RectPixel, meta: { firmante: TipoFirmante; page: number }): number {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return -1;
    }

    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === meta.firmante.codigo
    );
    if (firmanteIndex === -1) {
      return -1;
    }

    const norm = normalizedCoordsFromPixelRect(rect, this.medidaPdfViewport);
    const id = this.nextFirmaId();
    const firmanteGroup = this.firmantesNif.at(firmanteIndex) as FormGroup;
    const firmasGroup = firmanteGroup.get('firmas') as FormGroup;
    const posicionFirmas = this.ensurePosicionFirmasFormArray(firmasGroup);
    posicionFirmas.push(
      this.newPosFirmas({
        id,
        pagina: meta.page,
        firmante: meta.firmante,
        posicionTop: norm.posicionTop,
        posicionLeft: norm.posicionLeft,
        posicionRight: norm.posicionRight,
        posicionHeight: norm.posicionHeight,
        posicionWidth: norm.posicionWidth,
      })
    );

    this.refreshFormValues(posicionFirmas);
    this.refreshFormValues(firmasGroup);
    this.refreshFormValues(firmanteGroup);
    this.refreshFormValues(this.formularioDocumento);
    this.notifyFirmasStateChanged();
    return id;
  }

  removeFirmaById(id: number): void {
    const located = this.locateFirmaById(id);
    if (!located) {
      return;
    }

    const posicionFirmas = (this.firmantesNif.at(located.firmanteIndex) as FormGroup)
      .get('firmas')
      ?.get('posicionFirmas') as FormArray;
    posicionFirmas.removeAt(located.firmaIndex);

    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    this.notifyFirmasStateChanged();
  }

  clearAllPosicionFirmas(): void {
    this.firmantesNif.controls.forEach((firmanteCtrl, firmanteIndex) => {
      const posicionFirmas = (firmanteCtrl as FormGroup)
        .get('firmas')
        ?.get('posicionFirmas') as FormArray;
      while (posicionFirmas.length > 0) {
        posicionFirmas.removeAt(0);
      }
    });
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    this.notifyFirmasStateChanged();
  }

  removeFirmasForFirmante(firmanteCodigo: string): void {
    this.firmantesNif.controls.forEach((firmanteCtrl, firmanteIndex) => {
      const firmanteGroup = firmanteCtrl as FormGroup;
      const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
      if (tipoFirmante?.codigo !== firmanteCodigo) {
        return;
      }
      const posicionFirmas = firmanteGroup.get('firmas')?.get('posicionFirmas') as FormArray;
      while (posicionFirmas.length > 0) {
        posicionFirmas.removeAt(0);
      }
    });
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  deleteCampoFirma(firmante:TipoFirmante, campo:string){
    let newArray = this.firmantesNif.value
    const index = newArray.findIndex((x:FirmanteNif) => x.tipoFirmante.codigo == firmante.codigo)
    let firmaNif = this.firmantesNif.at(index) as FormGroup ;
    firmaNif.controls['firmas'].get(campo).reset();
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    this.notifyFirmasStateChanged();
  }

  private locateFirmaById(id: number): FirmaControlLocation | null {
    for (let firmanteIndex = 0; firmanteIndex < this.firmantesNif.length; firmanteIndex++) {
      const tipoFirmante = (this.firmantesNif.at(firmanteIndex) as FormGroup).get('tipoFirmante')
        ?.value as TipoFirmante;
      const located = locateFirmaControl(this.firmantesNif, id, tipoFirmante?.codigo);
      if (located) {
        return located;
      }
    }
    return null;
  }

  private ensurePosicionFirmasFormArray(firmasGroup: FormGroup): FormArray {
    const current = firmasGroup.get('posicionFirmas');
    if (current instanceof FormArray) {
      return current;
    }
    const formArray = this.fb.array([]);
    firmasGroup.setControl('posicionFirmas', formArray);
    return formArray;
  }

  private nextFirmaId(): number {
    const ids: number[] = [];
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const posicionFirmas = (firmanteCtrl as FormGroup)
        .get('firmas')
        ?.get('posicionFirmas') as FormArray;
      posicionFirmas?.value?.forEach((row: { id?: number }) => {
        if (row?.id != null) {
          ids.push(row.id);
        }
      });
    });
    return ids.length === 0 ? 0 : Math.max(...ids) + 1;
  }

  // ---------------------------------------------------------------------------
  // Casillas / formularios (paso 10)
  // ---------------------------------------------------------------------------

  /**
   * Persiste posicion de casilla en `firmantesNif[].formularios` (coordenadas normalizadas).
   */
  updateCasillaPosition(
    id: number,
    firmanteCodigo: string,
    rectPixel: RectPixel,
    patch?: UpdateCasillaPositionPatch
  ): boolean {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return false;
    }

    const located = locateCasillaControl(this.firmantesNif, id, firmanteCodigo);
    if (!located) {
      return false;
    }

    const norm = normalizedCoordsFromPixelRect(rectPixel, this.medidaPdfViewport);
    located.group.patchValue({
      posicionTop: norm.posicionTop,
      posicionLeft: norm.posicionLeft,
      posicionRight: norm.posicionRight,
      posicionHeight: norm.posicionHeight,
      posicionWidth: norm.posicionWidth,
      ...(patch?.page !== undefined ? { pagina: patch.page } : {}),
      ...(patch?.nombre !== undefined ? { nombre: patch.nombre } : {}),
      ...(patch?.orden !== undefined ? { orden: patch.orden } : {}),
      ...(patch?.valorOpcionSeleccionada !== undefined
        ? { valorOpcionSeleccionada: patch.valorOpcionSeleccionada }
        : {}),
      ...(patch?.obligatorio !== undefined ? { obligatorio: patch.obligatorio } : {}),
      ...(patch?.idNombreFormulario !== undefined
        ? { idNombreFormulario: patch.idNombreFormulario }
        : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    return true;
  }

  addCasillaFromPixel(
    rect: RectPixel,
    meta: {
      firmante: TipoFirmante;
      page: number;
      nombre: string;
      idNombreFormulario?: number | null;
      valorOpcionSeleccionada?: string;
      obligatorio?: boolean;
    }
  ): number {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return -1;
    }

    const resolved = this.resolveMetaForCasillaPlacement(meta);
    const norm = normalizedCoordsFromPixelRect(rect, this.medidaPdfViewport);
    const orden = this.nextOrdenForFormulario(
      resolved.firmante.codigo,
      resolved.nombre,
      resolved.idNombreFormulario
    );

    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === resolved.firmante.codigo
    );
    if (firmanteIndex === -1) {
      return -1;
    }

    const firmanteGroup = this.firmantesNif.at(firmanteIndex) as FormGroup;
    const formularios = firmanteGroup.get('formularios') as FormArray;

    this.removeUnplacedRowsForGrupo(
      formularios,
      resolved.firmante.codigo,
      resolved.idNombreFormulario,
      resolved.nombre
    );

    const id = this.nextCasillaId();
    const casilla: PosicionFormularioConFirmante = {
      id,
      pagina: resolved.page,
      firmante: resolved.firmante,
      posicionTop: norm.posicionTop,
      posicionLeft: norm.posicionLeft,
      posicionRight: norm.posicionRight,
      posicionHeight: norm.posicionHeight,
      posicionWidth: norm.posicionWidth,
      nombre: resolved.nombre,
      orden,
      valorOpcionSeleccionada: resolved.valorOpcionSeleccionada,
      obligatorio: resolved.obligatorio,
      idNombreFormulario: resolved.idNombreFormulario ?? undefined,
    };
    formularios.push(this.buildCasillaFormGroup(casilla, resolved.firmante));

    this.reorderCasillasOrdenEnFormulario();
    this.refreshFormValues(formularios);
    this.refreshFormValues(firmanteGroup);
    this.refreshFormValues(this.formularioDocumento);
    return id;
  }

  /** Elimina solo la fila colocada con ese id en firmantesNif[].formularios. */
  removeCasillaById(id: number): void {
    const located = this.locateCasillaById(id);
    if (!located) {
      return;
    }

    const snapshot = { ...located.group.value, firmante: located.tipoFirmante };
    const formularios = (this.firmantesNif.at(located.firmanteIndex) as FormGroup).get(
      'formularios'
    ) as FormArray;
    formularios.removeAt(located.casillaIndex);

    const remainingColocadas = this.countColocadasEnGrupoFormularios(
      formularios,
      snapshot.idNombreFormulario,
      snapshot.nombre
    );

    if (remainingColocadas === 0) {
      this.removeUnplacedRowsForGrupo(
        formularios,
        snapshot.firmante?.codigo,
        snapshot.idNombreFormulario,
        snapshot.nombre
      );
    }

    this.reorderCasillasOrdenEnFormulario();
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    this.datosConfiguracionDocumentosService.transferirDatosCasillaBorrada(id);
  }

  updateCasillaExtraInfo(
    id: number,
    patch: { orden?: number; valorOpcionSeleccionada?: string; obligatorio?: boolean }
  ): void {
    const located = this.locateCasillaById(id);
    if (!located) {
      return;
    }

    located.group.patchValue({
      ...(patch.orden !== undefined ? { orden: patch.orden } : {}),
      ...(patch.valorOpcionSeleccionada !== undefined
        ? { valorOpcionSeleccionada: patch.valorOpcionSeleccionada }
        : {}),
      ...(patch.obligatorio !== undefined ? { obligatorio: patch.obligatorio } : {}),
    });

    if (patch.obligatorio !== undefined) {
      const nombre = located.group.get('nombre')?.value;
      const idNombreFormulario = located.group.get('idNombreFormulario')?.value;
      const formularios = (this.firmantesNif.at(located.firmanteIndex) as FormGroup).get(
        'formularios'
      ) as FormArray;
      formularios.controls.forEach((ctrl) => {
        const row = ctrl as FormGroup;
        const value = row.value;
        const sameGroup =
          idNombreFormulario != null
            ? value.idNombreFormulario === idNombreFormulario
            : value.nombre === nombre;
        if (sameGroup) {
          row.patchValue({ obligatorio: patch.obligatorio });
        }
      });
    }

    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  private locateCasillaById(id: number): CasillaControlLocation | null {
    for (let firmanteIndex = 0; firmanteIndex < this.firmantesNif.length; firmanteIndex++) {
      const tipoFirmante = (this.firmantesNif.at(firmanteIndex) as FormGroup).get('tipoFirmante')
        ?.value as TipoFirmante;
      const located = locateCasillaControl(this.firmantesNif, id, tipoFirmante?.codigo);
      if (located) {
        return located;
      }
    }
    return null;
  }

  private nextCasillaId(): number {
    const ids: number[] = [];
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const formularios = (firmanteCtrl as FormGroup).get('formularios') as FormArray;
      formularios?.value?.forEach((row: { id?: number }) => {
        if (row?.id != null) {
          ids.push(row.id);
        }
      });
    });
    return ids.length === 0 ? 1 : Math.max(...ids) + 1;
  }

  private countColocadasEnGrupoFormularios(
    formularios: FormArray,
    idNombreFormulario: number | null | undefined,
    nombre: string | undefined
  ): number {
    let count = 0;
    formularios.controls.forEach((ctrl) => {
      const row = ctrl as FormGroup;
      if (
        this.mismaAgrupacionFormulario(row.value, idNombreFormulario, nombre) &&
        isCasillaRowColocada(row, this.medidaPdfViewport)
      ) {
        count++;
      }
    });
    return count;
  }

  private nextOrdenForFormulario(
    firmanteCodigo: string,
    nombre: string,
    idNombreFormulario?: number | null
  ): number {
    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === firmanteCodigo
    );
    if (firmanteIndex === -1) {
      return 1;
    }
    const formularios = (this.firmantesNif.at(firmanteIndex) as FormGroup).get('formularios') as FormArray;
    const mismoFormulario = (row: { nombre?: string; idNombreFormulario?: number }) => {
      if (idNombreFormulario != null) {
        return row.idNombreFormulario === idNombreFormulario;
      }
      return row.nombre === nombre;
    };
    const ordenes = formularios.value
      .filter(mismoFormulario)
      .map((row: { orden?: number }) => Number(row.orden))
      .filter((orden: number) => Number.isFinite(orden) && orden > 0);
    return ordenes.length > 0 ? Math.max(...ordenes) + 1 : 1;
  }

  private buildCasillaFormGroup(
    casilla: PosicionFormularioConFirmante,
    tipoFirmante: TipoFirmante
  ): FormGroup {
    return this.fb.group({
      id: [casilla.id],
      pagina: [casilla.pagina],
      firmante: [tipoFirmante],
      posicionTop: [casilla.posicionTop],
      posicionLeft: [casilla.posicionLeft],
      posicionRight: [casilla.posicionRight],
      posicionHeight: [casilla.posicionHeight],
      posicionWidth: [casilla.posicionWidth],
      orden: [casilla.orden],
      nombre: [casilla.nombre],
      valorOpcionSeleccionada: [casilla.valorOpcionSeleccionada ?? ''],
      obligatorio: [casilla.obligatorio ?? false],
      idNombreFormulario: [casilla.idNombreFormulario],
    });
  }

  private resolveMetaForCasillaPlacement(meta: {
    firmante: TipoFirmante;
    page: number;
    nombre: string;
    idNombreFormulario?: number | null;
    valorOpcionSeleccionada?: string;
    obligatorio?: boolean;
  }): {
    firmante: TipoFirmante;
    page: number;
    nombre: string;
    idNombreFormulario: number | null;
    valorOpcionSeleccionada: string;
    obligatorio: boolean;
  } {
    const idNombre = meta.idNombreFormulario ?? null;
    const borrador =
      idNombre != null ? this.findBorradorFormGroup(meta.firmante.codigo, idNombre) : null;
    return {
      firmante: meta.firmante,
      page: meta.page,
      nombre: (borrador?.get('nombre')?.value ?? meta.nombre ?? '').trim() || meta.nombre,
      idNombreFormulario: idNombre,
      valorOpcionSeleccionada:
        meta.valorOpcionSeleccionada ??
        borrador?.get('valorOpcionSeleccionadaPorDefecto')?.value ??
        '',
      obligatorio: meta.obligatorio ?? borrador?.get('obligatorio')?.value ?? false,
    };
  }

  /** Elimina filas del grupo en formularios que no tienen coords validas (legacy/semillas). */
  private removeUnplacedRowsForGrupo(
    formularios: FormArray,
    firmanteCodigo: string,
    idNombreFormulario: number | null,
    nombre: string
  ): void {
    for (let i = formularios.length - 1; i >= 0; i--) {
      const row = formularios.at(i) as FormGroup;
      if (row.get('firmante')?.value?.codigo !== firmanteCodigo) {
        continue;
      }
      if (!this.mismaAgrupacionFormulario(row.value, idNombreFormulario, nombre)) {
        continue;
      }
      if (!isCasillaRowColocada(row, this.medidaPdfViewport)) {
        formularios.removeAt(i);
      }
    }
  }

  private mismaAgrupacionFormulario(
    row: { idNombreFormulario?: number | null; nombre?: string },
    idNombreFormulario: number | null | undefined,
    nombre: string | undefined
  ): boolean {
    if (idNombreFormulario != null) {
      return row.idNombreFormulario != null && row.idNombreFormulario === idNombreFormulario;
    }
    const nombreBuscado = (nombre ?? '').trim();
    const nombreFila = (row.nombre ?? '').trim();
    return nombreBuscado.length > 0 && nombreFila === nombreBuscado;
  }

  /** Metadatos del grupo al que pertenece una casilla colocada (por id de fila). */
  getGrupoMetaForCasillaId(id: number): {
    firmanteCodigo: string;
    idNombreFormulario: number | null;
    nombre: string;
  } | null {
    const located = this.locateCasillaById(id);
    if (!located || !isCasillaRowColocada(located.group, this.medidaPdfViewport)) {
      return null;
    }
    const value = located.group.value;
    return {
      firmanteCodigo: located.tipoFirmante?.codigo ?? value.firmante?.codigo,
      idNombreFormulario: value.idNombreFormulario ?? null,
      nombre: value.nombre ?? '',
    };
  }

  /** True si la casilla es la unica colocada de su grupo (firmante + idNombreFormulario). */
  esUltimaCasillaColocadaDelFormulario(id: number): boolean {
    const located = this.locateCasillaById(id);
    if (!located || !isCasillaRowColocada(located.group, this.medidaPdfViewport)) {
      return false;
    }
    const formularios = (this.firmantesNif.at(located.firmanteIndex) as FormGroup).get(
      'formularios'
    ) as FormArray;
    const value = located.group.value;
    return (
      this.countColocadasEnGrupoFormularios(
        formularios,
        value.idNombreFormulario,
        value.nombre
      ) === 1
    );
  }

  reorderCasillasOrdenEnFormulario(): void {
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const formularios = (firmanteCtrl as FormGroup).get('formularios') as FormArray;
      const byKey = new Map<string, FormGroup[]>();
      formularios.controls.forEach((ctrl) => {
        const row = ctrl as FormGroup;
        const value = row.value;
        const key =
          value.idNombreFormulario != null
            ? [value.idNombreFormulario, value.firmante?.codigo].join('|')
            : [value.nombre, value.firmante?.codigo].filter((v: string) => v != null).join('|') ||
              String(value.nombre ?? '');
        if (!byKey.has(key)) {
          byKey.set(key, []);
        }
        byKey.get(key).push(row);
      });
      byKey.forEach((items) => {
        items.sort((a, b) => (a.value.orden ?? 0) - (b.value.orden ?? 0));
        items.forEach((item, index) => item.patchValue({ orden: index + 1 }));
      });
    });
    this.refreshFormValues(this.firmantesNif);
  }

  // Formularios — borradores (fase 1 refactor formularios/casillas)

  nextIdNombreFormulario(): number {
    const ids: number[] = [];
    const collect = (row: { idNombreFormulario?: number | null }) => {
      if (row.idNombreFormulario != null && Number.isFinite(Number(row.idNombreFormulario))) {
        ids.push(Number(row.idNombreFormulario));
      }
    };
    this.formulariosBorradorFormArray.value.forEach(collect);
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const formularios = (firmanteCtrl as FormGroup).get('formularios') as FormArray;
      formularios?.value?.forEach(collect);
    });
    return (ids.length > 0 ? Math.max(...ids) : 0) + 1;
  }

  private resolveTipoFirmanteFromInput(firmante: FirmanteNif | TipoFirmante): TipoFirmante {
    return (firmante as FirmanteNif).tipoFirmante ?? (firmante as TipoFirmante);
  }

  crearBorradorFormulario(firmante: FirmanteNif | TipoFirmante): FormGroup {
    const tipoFirmante = this.resolveTipoFirmanteFromInput(firmante);
    const idNombreFormulario = this.nextIdNombreFormulario();
    const borrador: FormularioGrupoBorrador = {
      idNombreFormulario,
      nombre: '',
      obligatorio: false,
      valorOpcionSeleccionadaPorDefecto: '',
      firmante: tipoFirmante,
    };
    const group = buildFormularioGrupoBorradorGroup(this.fb, borrador);
    this.formulariosBorradorFormArray.push(group);
    this.formulariosBorradorFormArray.updateValueAndValidity();
    return group;
  }

  findBorradorFormGroup(firmanteCodigo: string, idNombreFormulario: number): FormGroup | null {
    const found = this.formulariosBorradorFormArray.controls.find((ctrl) => {
      const row = ctrl as FormGroup;
      return (
        row.get('firmante')?.value?.codigo === firmanteCodigo &&
        row.get('idNombreFormulario')?.value === idNombreFormulario
      );
    });
    return (found as FormGroup) ?? null;
  }

  actualizarBorrador(firmanteCodigo: string, idNombreFormulario: number, patch: ActualizarFormularioGrupoBorradorPatch): void {
    const borrador = this.findBorradorFormGroup(firmanteCodigo, idNombreFormulario);
    if (!borrador) {
      return;
    }
    if (patch.nombre !== undefined) {
      borrador.get('nombre')?.setValue(patch.nombre);
    }
    if (patch.obligatorio !== undefined) {
      borrador.get('obligatorio')?.setValue(patch.obligatorio);
    }
    if (patch.valorOpcionSeleccionadaPorDefecto !== undefined) {
      borrador.get('valorOpcionSeleccionadaPorDefecto')?.setValue(patch.valorOpcionSeleccionadaPorDefecto);
    }

    const nombre = borrador.get('nombre')?.value ?? '';
    const obligatorio = borrador.get('obligatorio')?.value ?? false;
    const formularios = this.getFormulariosArrayForFirmanteCodigo(firmanteCodigo);
    if (formularios) {
      formularios.controls.forEach((ctrl) => {
        const row = ctrl as FormGroup;
        if (!this.mismaAgrupacionFormulario(row.value, idNombreFormulario, nombre)) {
          return;
        }
        row.patchValue({ nombre, obligatorio }, { emitEvent: false });
      });
      const firmanteIndex = this.firmantesNif.value.findIndex(
        (f: FirmanteNif) => f.tipoFirmante.codigo === firmanteCodigo
      );
      if (firmanteIndex >= 0) {
        this.refreshFormValues(this.firmantesNif.at(firmanteIndex) as FormGroup);
      }
    }
    this.formulariosBorradorFormArray.updateValueAndValidity();
  }

  countCasillasColocadasEnGrupo(firmanteCodigo: string, idNombreFormulario: number): number {
    const formularios = this.getFormulariosArrayForFirmanteCodigo(firmanteCodigo);
    if (!formularios) {
      return 0;
    }
    const borrador = this.findBorradorFormGroup(firmanteCodigo, idNombreFormulario);
    const nombre = borrador?.get('nombre')?.value ?? '';
    return this.countColocadasEnGrupoFormularios(formularios, idNombreFormulario, nombre);
  }

  removeBorradorFormulario(firmanteCodigo: string, idNombreFormulario: number): void {
    const index = this.formulariosBorradorFormArray.controls.findIndex((ctrl) => {
      const row = ctrl as FormGroup;
      return (
        row.get('firmante')?.value?.codigo === firmanteCodigo &&
        row.get('idNombreFormulario')?.value === idNombreFormulario
      );
    });
    if (index >= 0) {
      this.formulariosBorradorFormArray.removeAt(index);
      this.formulariosBorradorFormArray.updateValueAndValidity();
    }
  }

  /** Reconstruye borradores desde casillas colocadas en `firmantesNif[].formularios`. */
  hydrateBorradoresFromFormularios(): void {
    this.formulariosBorradorFormArray = new FormArray([]);
    const metaPorGrupo = new Map<
      string,
      FormularioGrupoBorrador
    >();

    const upsertMeta = (partial: FormularioGrupoBorrador) => {
      const key = `${partial.firmante.codigo}|${partial.idNombreFormulario}`;
      const existing = metaPorGrupo.get(key);
      if (!existing) {
        metaPorGrupo.set(key, { ...partial });
        return;
      }
      if ((partial.nombre ?? '').trim().length > 0) {
        existing.nombre = partial.nombre;
      }
      existing.obligatorio = partial.obligatorio;
      if ((partial.valorOpcionSeleccionadaPorDefecto ?? '').length > 0) {
        existing.valorOpcionSeleccionadaPorDefecto = partial.valorOpcionSeleccionadaPorDefecto;
      }
    };

    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const firmanteGroup = firmanteCtrl as FormGroup;
      const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
      if (!tipoFirmante?.codigo) {
        return;
      }
      const formularios = firmanteGroup.get('formularios') as FormArray;
      formularios?.controls.forEach((ctrl) => {
        const row = ctrl as FormGroup;
        if (!isCasillaRowColocada(row, this.medidaPdfViewport)) {
          return;
        }
        const idNombre = row.get('idNombreFormulario')?.value;
        if (idNombre == null) {
          return;
        }
        upsertMeta({
          idNombreFormulario: Number(idNombre),
          nombre: row.get('nombre')?.value ?? '',
          obligatorio: row.get('obligatorio')?.value ?? false,
          valorOpcionSeleccionadaPorDefecto: row.get('valorOpcionSeleccionada')?.value ?? '',
          firmante: tipoFirmante,
        });
      });
    });

    metaPorGrupo.forEach((borrador) => {
      this.formulariosBorradorFormArray.push(buildFormularioGrupoBorradorGroup(this.fb, borrador));
    });
    this.formulariosBorradorFormArray.updateValueAndValidity();
  }

  getBorradoresForFirmante(firmanteCodigo: string): FormularioGrupoBorrador[] {
    return this.formulariosBorradorFormArray.controls
      .map((ctrl) => readFormularioGrupoBorrador(ctrl as FormGroup))
      .filter((b) => b.firmante?.codigo === firmanteCodigo);
  }

  getBorradorFormGroupsForFirmante(firmanteCodigo: string): FormGroup[] {
    return this.formulariosBorradorFormArray.controls
      .filter((ctrl) => (ctrl as FormGroup).get('firmante')?.value?.codigo === firmanteCodigo)
      .map((ctrl) => ctrl as FormGroup);
  }

  firmanteTieneBorradoresFormulario(firmanteCodigo: string): boolean {
    return this.getBorradorFormGroupsForFirmante(firmanteCodigo).length > 0;
  }

  /** Borradores con nombre pero sin ninguna casilla colocada (bloquean Siguiente en paso 10). */
  getFormulariosIncompletosPaso10(): Array<{firmanteCodigo: string; firmanteDesc: string; nombre: string; idNombreFormulario: number;}> {
    const incompletos: Array<{firmanteCodigo: string; firmanteDesc: string; nombre: string; idNombreFormulario: number;}> = [];
    this.formulariosBorradorFormArray.controls.forEach((ctrl) => {
      const row = ctrl as FormGroup;
      const nombre = (row.get('nombre')?.value ?? '').trim();
      if (!nombre) {
        return;
      }
      const firmante = row.get('firmante')?.value;
      const idNombre = row.get('idNombreFormulario')?.value;
      if (firmante?.codigo == null || idNombre == null) {
        return;
      }
      if (this.countCasillasColocadasEnGrupo(firmante.codigo, idNombre) < 1) {
        incompletos.push({
          firmanteCodigo: firmante.codigo,
          firmanteDesc: firmante.desc ?? firmante.codigo,
          nombre,
          idNombreFormulario: idNombre,
        });
      }
    });
    return incompletos;
  }

  /** Elimina borradores sin nombre y sin casillas colocadas (no bloquean Siguiente). */
  pruneBorradoresVaciosSinNombre(): void {
    for (let i = this.formulariosBorradorFormArray.length - 1; i >= 0; i--) {
      const row = this.formulariosBorradorFormArray.at(i) as FormGroup;
      const nombre = (row.get('nombre')?.value ?? '').trim();
      if (nombre) {
        continue;
      }
      const firmante = row.get('firmante')?.value;
      const idNombre = row.get('idNombreFormulario')?.value;
      const colocadas =
        firmante?.codigo != null && idNombre != null
          ? this.countCasillasColocadasEnGrupo(firmante.codigo, idNombre)
          : 0;
      if (colocadas === 0) {
        this.formulariosBorradorFormArray.removeAt(i);
      }
    }
    this.formulariosBorradorFormArray.updateValueAndValidity();
  }

  // Formularios
  addFormCasillas(firmante: FirmanteNif): FormGroup {
    return this.crearBorradorFormulario(firmante);
  }

  setExisitingFormulariosArray(firmanteNif: FirmanteNif | null) {
    const newFormArray = new FormArray([]);
    if (firmanteNif?.formularios && firmanteNif.firmas.tipoFirma !== TipoFirma.Validador) {
      const idsNombreExistentes = firmanteNif.formularios
        .map((row) => row?.idNombreFormulario)
        .filter((id) => id != null)
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      let nextIdNombre = idsNombreExistentes.length > 0 ? Math.max(...idsNombreExistentes) : 0;
      const idNombrePorGrupo = new Map<string, number>();

      firmanteNif.formularios.forEach((x, indexFormulario) => {
        const nombreNormalizado = (x?.nombre ?? '').toString().trim();
        const keyGrupo =
          nombreNormalizado !== ''
            ? `${firmanteNif.tipoFirmante?.codigo ?? ''}|${nombreNormalizado}`
            : `${firmanteNif.tipoFirmante?.codigo ?? ''}|__sin_nombre__|${x?.id ?? indexFormulario}`;

        let idNombreFormulario = x?.idNombreFormulario;
        if (idNombreFormulario == null) {
          if (!idNombrePorGrupo.has(keyGrupo)) {
            nextIdNombre++;
            idNombrePorGrupo.set(keyGrupo, nextIdNombre);
          }
          idNombreFormulario = idNombrePorGrupo.get(keyGrupo);
        } else {
          const idNum = Number(idNombreFormulario);
          if (Number.isFinite(idNum)) {
            idNombreFormulario = idNum;
            if (!idNombrePorGrupo.has(keyGrupo)) {
              idNombrePorGrupo.set(keyGrupo, idNum);
            }
            if (idNum > nextIdNombre) {
              nextIdNombre = idNum;
            }
          }
        }

        newFormArray.push(
          this.fb.group({
            id: [x.id],
            nombre: [x.nombre],
            idNombreFormulario: [idNombreFormulario],
            orden: [x.orden],
            pagina: [x.pagina],
            posicionTop: [x.posicionTop],
            posicionLeft: [x.posicionLeft],
            posicionRight: [x.posicionRight],
            posicionHeight: [x.posicionHeight],
            posicionWidth: [x.posicionWidth],
            firmante: [firmanteNif.tipoFirmante],
            valorOpcionSeleccionada: [x.valorOpcionSeleccionada],
            obligatorio: [x.obligatorio],
          })
        );
      });
    }
    return newFormArray;
  }

  /** Borra un grupo de formulario (filas colocadas, verificacion y borrador). Mismo camino que confirmar ultima casilla. */
  deleteFormularioGrupo(
    firmanteCodigo: string,
    idNombreFormulario: number,
    nombre: string,
    idCasillaClicked?: number
  ): void {
    this.deleteFormCasillas(idNombreFormulario, nombre, idCasillaClicked, firmanteCodigo);
  }

  deleteFormCasillas(
    idNombreFormulario: number,
    nombre: string,
    _idCasillaClicked?: number,
    firmanteCodigo?: string
  ) {
    if (firmanteCodigo == null || idNombreFormulario == null) {
      return;
    }
    const idsBorrados = this.removeFormularioGrupoFromFormularios(
      firmanteCodigo,
      idNombreFormulario,
      nombre
    );
    this.removeBorradorFormulario(firmanteCodigo, idNombreFormulario);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    idsBorrados.forEach((idCasilla) =>
      this.datosConfiguracionDocumentosService.transferirDatosCasillaBorrada(idCasilla)
    );
    this.datosConfiguracionDocumentosService.transferirArrayFormulariosVacio(null);
  }

  private removeFormularioGrupoFromFormularios(
    firmanteCodigo: string | undefined,
    idNombreFormulario: number | null | undefined,
    nombre: string
  ): number[] {
    const idsBorrados: number[] = [];
    const removeFromFormularios = (formularios: FormArray) => {
      for (let i = formularios.length - 1; i >= 0; i--) {
        const row = formularios.at(i) as FormGroup;
        if (!this.mismaAgrupacionFormulario(row.value, idNombreFormulario, nombre)) {
          continue;
        }
        const idCasilla = row.get('id')?.value;
        if (idCasilla != null) {
          idsBorrados.push(idCasilla);
        }
        formularios.removeAt(i);
      }
    };

    if (firmanteCodigo != null) {
      const firmanteIndex = this.firmantesNif.value.findIndex(
        (f: FirmanteNif) => f.tipoFirmante.codigo === firmanteCodigo
      );
      if (firmanteIndex >= 0) {
        const firmanteGroup = this.firmantesNif.at(firmanteIndex) as FormGroup;
        removeFromFormularios(firmanteGroup.get('formularios') as FormArray);
        this.refreshFormValues(firmanteGroup);
      }
    } else {
      this.firmantesNif.controls.forEach((firmanteControl) => {
        const firmanteGroup = firmanteControl as FormGroup;
        removeFromFormularios(firmanteGroup.get('formularios') as FormArray);
        this.refreshFormValues(firmanteGroup);
      });
    }

    return idsBorrados;
  }

  /**
   * Ensures a formulario has idNombreFormulario. If missing, assigns one and propagates it
   * to the clicked row.
   */
  ensureIdNombreFormulario(idCasilla: number, firmanteCodigo: string, nombre: string): number {
    const located = this.locateCasillaById(idCasilla);
    if (!located) {
      return null;
    }
    const existing = located.group.get('idNombreFormulario')?.value;
    if (existing != null) {
      return existing;
    }
    const newIdNombre = this.nextIdNombreFormulario();
    this.propagateIdNombreFormularioEnGrupo(firmanteCodigo, idCasilla, newIdNombre, nombre);
    this.refreshFormValues(this.formularioDocumento);
    return newIdNombre;
  }

  /** Asigna idNombreFormulario al grupo sin reconstruir firmantesNif[].formularios. */
  private propagateIdNombreFormularioEnGrupo(
    firmanteCodigo: string,
    idCasilla: number,
    idNombreFormulario: number,
    nombre: string
  ): void {
    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === firmanteCodigo
    );
    if (firmanteIndex === -1) {
      return;
    }
    const formularios = (this.firmantesNif.at(firmanteIndex) as FormGroup).get('formularios') as FormArray;
    formularios.controls.forEach((ctrl) => {
      const row = ctrl as FormGroup;
      const rowId = row.get('id')?.value;
      const rowNombre = (row.get('nombre')?.value ?? '').trim();
      const mismoId = rowId === idCasilla;
      const mismoNombreLegacy =
        row.get('idNombreFormulario')?.value == null &&
        nombre.trim().length > 0 &&
        rowNombre === nombre.trim();
      if (mismoId || mismoNombreLegacy) {
        row.get('idNombreFormulario')?.setValue(idNombreFormulario);
      }
    });
    this.refreshFormValues(this.firmantesNif.at(firmanteIndex) as FormGroup);
  }

  /**
   * Returns an idNombreFormulario safe for deleting one formulario.
   * If the clicked row has null idNombre, creates one.
   * If idNombre is shared by multiple empty rows (legacy/merge artifact), isolates clicked row.
   */
  resolveIdNombreFormularioForDelete(
    idCasilla: number,
    idNombreFormulario: number,
    firmanteCodigo: string,
    nombre: string
  ): number {
    if (idNombreFormulario != null && (idCasilla == null || idCasilla === idNombreFormulario)) {
      return idNombreFormulario;
    }
    if (idNombreFormulario == null) {
      return this.ensureIdNombreFormulario(idCasilla, firmanteCodigo, nombre);
    }
    return idNombreFormulario;
  }

  private getFormulariosArrayForFirmanteCodigo(firmanteCodigo: string): FormArray | null {
    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === firmanteCodigo
    );
    if (firmanteIndex === -1) {
      return null;
    }
    return (this.firmantesNif.at(firmanteIndex) as FormGroup).get('formularios') as FormArray;
  }


  // ---------------------------------------------------------------------------
  // Campos editables (paso 11)
  // ---------------------------------------------------------------------------

  //cargamos cada firmanteNif que viene de la api y añadimos el array de camposEditables al correspondiente firmanteNif del form.
  setExisitingCamposArray(firmanteNif: FirmanteNif){
    let newFormArray = new FormArray([]);
    if(firmanteNif.camposEditables && firmanteNif.firmas.tipoFirma!==TipoFirma.Validador){ //solo añadimos al formulario los campos editables que su tipo de firma no sea validador.
      firmanteNif.camposEditables.forEach(x=>{
        const newForm = this.fb.group({
          id: [x.id],
          pagina: [x.pagina],
          posicionTop: [x.posicionTop],
          posicionLeft: [x.posicionLeft],
          posicionRight: [x.posicionRight],
          posicionHeight: [x.posicionHeight],
          posicionWidth: [x.posicionWidth],
          firmante:[firmanteNif.tipoFirmante],
          identificador: [(!x.identificador || x.identificador === '') ? `Texto_${x.id}` : x.identificador],
          nombreCampo: [x.nombreCampo ?? ''],
          valorPorDefecto: [x.valorPorDefecto ?? ''],
          obligatorio: [x.obligatorio ?? false]
        })
        newFormArray.push(newForm);
      })
    }
    return newFormArray
  }

  //Campos Editables

  updateCampoPosition(
    id: number,
    firmanteCodigo: string,
    rectPixel: RectPixel,
    patch?: UpdateCampoPositionPatch
  ): boolean {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return false;
    }

    const located = locateCampoControl(this.firmantesNif, id, firmanteCodigo);
    if (!located) {
      return false;
    }

    const norm = normalizedCoordsFromPixelRect(rectPixel, this.medidaPdfViewport);
    located.group.patchValue({
      posicionTop: norm.posicionTop,
      posicionLeft: norm.posicionLeft,
      posicionRight: norm.posicionRight,
      posicionHeight: norm.posicionHeight,
      posicionWidth: norm.posicionWidth,
      ...(patch?.page !== undefined ? { pagina: patch.page } : {}),
      ...(patch?.identificador !== undefined ? { identificador: patch.identificador } : {}),
      ...(patch?.nombreCampo !== undefined ? { nombreCampo: patch.nombreCampo } : {}),
      ...(patch?.valorPorDefecto !== undefined ? { valorPorDefecto: patch.valorPorDefecto } : {}),
      ...(patch?.obligatorio !== undefined ? { obligatorio: patch.obligatorio } : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
    return true;
  }

  addCampoFromPixel(
    rect: RectPixel,
    meta: {
      firmante: TipoFirmante;
      page: number;
      identificador?: string;
      nombreCampo?: string;
      valorPorDefecto?: string;
      obligatorio?: boolean;
    }
  ): number {
    if (!this.medidaPdfViewport || !this.formularioDocumento) {
      return -1;
    }

    const firmanteIndex = this.firmantesNif.value.findIndex(
      (f: FirmanteNif) => f.tipoFirmante.codigo === meta.firmante.codigo
    );
    if (firmanteIndex === -1) {
      return -1;
    }

    const norm = normalizedCoordsFromPixelRect(rect, this.medidaPdfViewport);
    const id = this.nextCampoId();
    const identificador = (meta.identificador ?? '').trim() || `Texto_${id}`;

    const firmanteGroup = this.firmantesNif.at(firmanteIndex) as FormGroup;
    const camposEditables = firmanteGroup.get('camposEditables') as FormArray;
    camposEditables.push(
      this.fb.group({
        id: [id],
        pagina: [meta.page],
        firmante: [meta.firmante],
        posicionTop: [norm.posicionTop],
        posicionLeft: [norm.posicionLeft],
        posicionRight: [norm.posicionRight],
        posicionHeight: [norm.posicionHeight],
        posicionWidth: [norm.posicionWidth],
        identificador: [identificador],
        nombreCampo: [meta.nombreCampo ?? ''],
        valorPorDefecto: [meta.valorPorDefecto ?? ''],
        obligatorio: [meta.obligatorio ?? false],
      })
    );

    this.refreshFormValues(camposEditables);
    this.refreshFormValues(firmanteGroup);
    this.refreshFormValues(this.formularioDocumento);
    return id;
  }

  removeCampoById(id: number): void {
    const located = this.locateCampoById(id);
    if (!located) {
      return;
    }

    const camposEditables = (this.firmantesNif.at(located.firmanteIndex) as FormGroup).get(
      'camposEditables'
    ) as FormArray;
    camposEditables.removeAt(located.campoIndex);

    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  updateCampoExtraInfo(
    id: number,
    patch: {
      identificador?: string;
      nombreCampo?: string;
      valorPorDefecto?: string;
      obligatorio?: boolean;
    }
  ): void {
    const located = this.locateCampoById(id);
    if (!located) {
      return;
    }

    located.group.patchValue({
      ...(patch.identificador !== undefined ? { identificador: patch.identificador } : {}),
      ...(patch.nombreCampo !== undefined ? { nombreCampo: patch.nombreCampo } : {}),
      ...(patch.valorPorDefecto !== undefined ? { valorPorDefecto: patch.valorPorDefecto } : {}),
      ...(patch.obligatorio !== undefined ? { obligatorio: patch.obligatorio } : {}),
    });

    this.refreshFormValues(located.group);
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  clearAllCamposEditables(): void {
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const camposEditables = (firmanteCtrl as FormGroup).get('camposEditables') as FormArray;
      while (camposEditables.length > 0) {
        camposEditables.removeAt(0);
      }
    });
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  removeCamposForFirmante(firmanteCodigo: string): void {
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const firmanteGroup = firmanteCtrl as FormGroup;
      const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
      if (tipoFirmante?.codigo !== firmanteCodigo) {
        return;
      }
      const camposEditables = firmanteGroup.get('camposEditables') as FormArray;
      while (camposEditables.length > 0) {
        camposEditables.removeAt(0);
      }
    });
    this.refreshFormValues(this.firmantesNif);
    this.refreshFormValues(this.formularioDocumento);
  }

  private locateCampoById(id: number): CampoControlLocation | null {
    for (let firmanteIndex = 0; firmanteIndex < this.firmantesNif.length; firmanteIndex++) {
      const tipoFirmante = (this.firmantesNif.at(firmanteIndex) as FormGroup).get('tipoFirmante')
        ?.value as TipoFirmante;
      const located = locateCampoControl(this.firmantesNif, id, tipoFirmante?.codigo);
      if (located) {
        return located;
      }
    }
    return null;
  }

  private nextCampoId(): number {
    const ids: number[] = [];
    this.firmantesNif.controls.forEach((firmanteCtrl) => {
      const camposEditables = (firmanteCtrl as FormGroup).get('camposEditables') as FormArray;
      camposEditables?.value?.forEach((row: { id?: number }) => {
        if (row?.id != null) {
          ids.push(row.id);
        }
      });
    });
    return ids.length === 0 ? 0 : Math.max(...ids) + 1;
  }

  // ---------------------------------------------------------------------------
  // Guardado y utilidades
  // ---------------------------------------------------------------------------

  // General
  montarObjetoDocumentoGuardar(){
    if(this.formularioDocumento){
      this.objetoDocumentoEnviar.cifOrden = this.formularioDocumento.controls['cifOrden'].value;
      this.objetoDocumentoEnviar.cifPage = this.formularioDocumento.controls['cifPage'].value;
      this.objetoDocumentoEnviar.cifFormulario = this.formularioDocumento.controls['cifFormulario'].value;
      // this.objetoDocumentoEnviar.contieneCif = this.formularioDocumento.controls['contieneCif'].value;
      // this.objetoDocumentoEnviar.copyConfiguracion = this.formularioDocumento.controls['copyConfiguracion'].value;
      this.objetoDocumentoEnviar.generico = this.formularioDocumento.controls['generico'].value;
      this.objetoDocumentoEnviar.idPdf = this.formularioDocumento.controls['idPdf'].value;
      this.objetoDocumentoEnviar.idTipoDocOrigen = this.formularioDocumento.controls['idTipoDocOrigen'].value;
      this.objetoDocumentoEnviar.idTipoDocumento = this.formularioDocumento.controls['idTipoDocumento'].value;
      this.objetoDocumentoEnviar.incluirContenido = this.formularioDocumento.controls['incluirContenido'].value;
      this.objetoDocumentoEnviar.nombre = this.formularioDocumento.controls['nombre'].value.trim();
      this.objetoDocumentoEnviar.texto = this.formularioDocumento.controls['texto'].value;
      this.objetoDocumentoEnviar.textoAExcluir = this.formularioDocumento.controls['textoAExcluir'].value;
      this.objetoDocumentoEnviar.tiposContenido = [];
      this.tiposContenido.controls.forEach((ctrl) => {
        const row = ctrl as FormGroup;
        if (!isPalabraRowColocada(row, this.medidaPdfViewport)) {
          return;
        }
        this.objetoDocumentoEnviar.tiposContenido.push(row.value);
      });
      // this.objetoDocumentoEnviar.urlDocTemp = this.formularioDocumento.controls['urlDocTemp'].value;
      this.objetoDocumentoEnviar.paginas = this.formularioDocumento.controls['paginas'].value;
      this.objetoDocumentoEnviar.esCopiaBasica = this.formularioDocumento.controls['esCopiaBasica'].value;
      this.objetoDocumentoEnviar.split = this.formularioDocumento.controls['split'].value;
      this.objetoDocumentoEnviar.formatoNombreArchivoAuditTrail = this.formularioDocumento.controls['formatoNombreArchivoAuditTrail'].value;
      this.objetoDocumentoEnviar.formatoNombreArchivoFirmado = this.formularioDocumento.controls['formatoNombreArchivoFirmado'].value
      this.objetoDocumentoEnviar.firmantesNif = [];
      this.objetoDocumentoEnviar.textoFormulario= this.formularioDocumento.controls['textoFormulario'].value;
      this.objetoDocumentoEnviar.textoAExcluirFormulario = this.formularioDocumento.controls['textoAExcluirFormulario'].value;
      this.objetoDocumentoEnviar.esRLT = this.formularioDocumento.controls['esRLT'].value;

      this.firmantesNif.controls.forEach(x=>{
        let newFirmanteNif: FirmanteNif = {} as FirmanteNif;
        newFirmanteNif.tipoFirmante = ((x as FormGroup).controls['tipoFirmante'] as FormGroup).value;
        newFirmanteNif.nifOrden = (x as FormGroup).controls['nifOrden'].value;
        newFirmanteNif.nifFormulario = (x as FormGroup).controls['nifFormulario'].value;
        newFirmanteNif.nifPage = (x as FormGroup).controls['nifPage'].value;
        newFirmanteNif.firmas = this.convierteFirmantes((x as FormGroup).controls['firmas'] as FormGroup);
        newFirmanteNif.formularios = [];

        let formulariosArray = (x as FormGroup).controls['formularios'] as FormArray;
        formulariosArray.controls.forEach((y) => {
          const row = y as FormGroup;
          if (!isCasillaRowColocada(row, this.medidaPdfViewport)) {
            return;
          }
          const formNuevo: PosicionFormulario = {} as PosicionFormulario;
          formNuevo.id = row.controls['id'].value;
          formNuevo.orden = row.controls['orden'].value;
          formNuevo.nombre = row.controls['nombre'].value;
          formNuevo.idNombreFormulario = row.controls['idNombreFormulario']?.value;
          formNuevo.pagina = row.controls['pagina'].value;
          formNuevo.posicionTop = row.controls['posicionTop'].value;
          formNuevo.posicionLeft = row.controls['posicionLeft'].value;
          formNuevo.posicionRight = row.controls['posicionRight'].value;
          formNuevo.posicionHeight = row.controls['posicionHeight'].value;
          formNuevo.posicionWidth = row.controls['posicionWidth'].value;
          formNuevo.valorOpcionSeleccionada = row.controls['valorOpcionSeleccionada'].value;
          formNuevo.obligatorio = row.controls['obligatorio'].value;
          newFirmanteNif.formularios.push(formNuevo);
        });

        newFirmanteNif.camposEditables = [];
        const camposArray = (x as FormGroup).controls['camposEditables'] as FormArray;
        camposArray.controls.forEach((campoCtrl) => {
          const row = campoCtrl as FormGroup;
          if (!isCampoRowColocada(row, this.medidaPdfViewport)) {
            return;
          }
          const formNuevo: PosicionFormulario = {} as PosicionFormulario;
          formNuevo.id = row.controls['id'].value;
          formNuevo.pagina = row.controls['pagina'].value;
          formNuevo.posicionTop = row.controls['posicionTop'].value;
          formNuevo.posicionLeft = row.controls['posicionLeft'].value;
          formNuevo.posicionRight = row.controls['posicionRight'].value;
          formNuevo.posicionHeight = row.controls['posicionHeight'].value;
          formNuevo.posicionWidth = row.controls['posicionWidth'].value;
          formNuevo.identificador = row.controls['identificador'].value;
          formNuevo.nombreCampo = row.controls['nombreCampo'].value;
          formNuevo.valorPorDefecto = row.controls['valorPorDefecto'].value;
          formNuevo.obligatorio = row.controls['obligatorio'].value;
          newFirmanteNif.camposEditables.push(formNuevo);
        });
        this.objetoDocumentoEnviar.firmantesNif.push(newFirmanteNif)
      })
    }
    return this.objetoDocumentoEnviar
  }

  convierteFirmantes(x: FormGroup): ConfiguracionFirma {
    const posicionFirmas = x.get('posicionFirmas') as FormArray;
    const colocadas =
      posicionFirmas?.controls
        ?.filter((row) => isFirmaRowColocada(row as FormGroup, this.medidaPdfViewport))
        ?.map((row) => (row as FormGroup).value) ?? [];
    const nombreCertificado = x.controls['nombreCertificado'].value;
    return {
      tipoFirma: x.controls['tipoFirma'].value,
      nombreCertificado:
        typeof nombreCertificado === 'string' ? nombreCertificado.trim() || null : nombreCertificado,
      tieneTag: x.controls['tieneTag'].value,
      nombreTag: x.controls['nombreTag'].value,
      posicionFirmas: colocadas,
    };
  }

  hayCambios(): boolean {
    const baseline = this.clonarSnapshotSinPaginas(this.documentoAEditar);
    const actual = this.clonarSnapshotSinPaginas(this.montarObjetoDocumentoGuardar());
    return JSON.stringify(baseline) !== JSON.stringify(actual);
  }

  private clonarSnapshotSinPaginas(doc: TipoDocumento): TipoDocumento {
    const clone = JSON.parse(JSON.stringify(doc ?? {}));
    delete clone.paginas;
    return clone;
  }

  checkSiDibujos(paginasNuevas:number):boolean{
    let result: boolean[] =[];
    this.oldDoc.firmantesNif.forEach(x=>{
      x.camposEditables?.forEach(y=>{
        if(y.pagina>paginasNuevas) result.push(true)
      })
      x.formularios?.forEach(y=>{
        if(y.pagina>paginasNuevas) result.push(true)
      })
      x.firmas.posicionFirmas?.forEach(y=>{
        if(y.pagina>paginasNuevas) result.push(true)
      })
    })
    this.oldDoc.tiposContenido?.forEach(x=>{
      if(x.pagina>paginasNuevas) result.push(true)
    })
    return result.includes(true)
  }

  /** TODO: quitar tras prueba manual RLT */
  private static readonly NOMBRE_DOC_PRUEBA_RLT = '3101 RLT';

  private aplicarParchePruebaRltEnDocumentosAll(): void {
    this.documentosAll?.forEach((doc) => {
      if (doc.nombre === FormularioDocumentoService.NOMBRE_DOC_PRUEBA_RLT) {
        doc.esRLT = true;
      }
    });
  }

}
